/**
 * StrategyExportService
 *
 * Exports strategy and block payloads as JSON files for offline
 * prompt testing and debugging outside builder-service.
 *
 * Export types:
 * - Strategy: Full AgentCreateStrategyRequest payload (after hydration)
 * - Block: Full AgentUpdateBlockRequest payload (after hydration)
 *
 * @module strategy-blocks/services
 */

import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { StrategyBlockService } from '@modules/firebase-core/services/StrategyBlockService';
import { StrategyStepNodeService } from '@modules/firebase-core/services/StrategyStepNodeService';
import type { AgentCreateStrategyRequest, AgentUpdateBlockRequest } from '@modules/shared-types/src/api.types';
import { cleanSourceDataForAI } from '../../builder-service/src/agent/utils/aiInputCleaners';
import { buildBlockLibraryCaptureFromData } from './blockLibraryCapture';
import type { BlockLibraryCapture } from './blockLibraryCapture';
import {
  sanitizeStrategyBlockForRegenerationInput,
  sanitizeStrategyScreenForRegenerationInput,
} from '@modules/strategy-blocks/utils/assetPlanWorkspaceSanitizer';

// ============================================================================
// Envelope Types
// ============================================================================

export interface StrategyExportEnvelope {
  __segmently_strategy_export__: true;
  version: '1.0.0';
  type: 'create-strategy';
  payload: AgentCreateStrategyRequest;
  metadata: {
    exportedAt: string;
    projectId: string;
    strategyId: string;
  };
}

export type StrategyAssetWorkspaceMode = 'regeneration' | 'portable';

export interface StrategyExportOptions {
  assetWorkspace?: StrategyAssetWorkspaceMode;
}

export interface BlockExportEnvelope {
  __segmently_block_export__: true;
  version: '1.0.0';
  type: 'update-block';
  payload: AgentUpdateBlockRequest;
  metadata: {
    exportedAt: string;
    projectId: string;
    strategyId: string;
    blockId: string;
    blockName?: string;
    assetWorkspace?: StrategyAssetWorkspaceMode;
  };
}

// Capture types moved to the pure core (shared with the fixture export
// script); re-exported here so existing dialog imports keep working.
export type { BlockLibraryCapture, BlockLibraryCaptureSummary } from './blockLibraryCapture';

// ============================================================================
// Service
// ============================================================================

export class StrategyExportService {
  /**
   * Export strategy as AgentCreateStrategyRequest JSON.
   *
   * Loads strategy document, its sourceData, and variables.
   * Produces the exact JSON that would go to POST /api/agent/create-strategy.
   */
  async exportStrategyPayload(
    projectId: string,
    strategyId: string,
    userId: string = 'export',
  ): Promise<StrategyExportEnvelope> {
    const strategyService = new StrategyService(projectId);
    const strategy = await strategyService.get(strategyId);

    if (!strategy) {
      throw new Error(`Strategy ${strategyId} not found`);
    }

    // Build the request payload matching AgentCreateStrategyRequest
    const payload: AgentCreateStrategyRequest = {
      projectId,
      strategyId,
      userId,
      name: strategy.name || 'Exported Strategy',
      description: strategy.description,
      strategyType: strategy.strategyType || 'IOS Onboarding',
      toneOfVoice: strategy.toneOfVoice,
      designDescription: strategy.designDescription,
      sourceData: cleanSourceDataForAI((strategy as any).sourceData ?? {}) as any,
      strategyVariables: (strategy as any).variables,
    };

    return {
      __segmently_strategy_export__: true,
      version: '1.0.0',
      type: 'create-strategy',
      payload,
      metadata: {
        exportedAt: new Date().toISOString(),
        projectId,
        strategyId,
      },
    };
  }

  /**
   * Export block as AgentUpdateBlockRequest JSON.
   *
   * Loads block, its screens (as currentBlock), strategy sourceData and variables.
   * Produces the exact JSON that would go to POST /api/agent/update-block.
   */
  async exportBlockPayload(
    projectId: string,
    strategyId: string,
    blockId: string,
    prompt: string = 'Regenerate this block',
    userId: string = 'export',
    options: StrategyExportOptions = {},
  ): Promise<BlockExportEnvelope> {
    const strategyService = new StrategyService(projectId);
    const blockService = new StrategyBlockService(projectId, strategyId);
    const screenService = new StrategyStepNodeService(projectId, strategyId, blockId);

    // Load strategy, block, and screens in parallel
    const [strategy, block, screens] = await Promise.all([
      strategyService.get(strategyId),
      blockService.get(blockId),
      screenService.listScreens(),
    ]);

    if (!strategy) throw new Error(`Strategy ${strategyId} not found`);
    if (!block) throw new Error(`Block ${blockId} not found`);

    const assetWorkspace = options.assetWorkspace ?? 'regeneration';

    // Build currentBlock with screens
    const currentBlock: Record<string, unknown> = {
      ...(assetWorkspace === 'portable'
        ? (block as unknown as Record<string, unknown>)
        : sanitizeStrategyBlockForRegenerationInput(block as any)),
      screens: screens.map((s) => {
        if (assetWorkspace === 'portable') {
          return { ...(s as unknown as Record<string, unknown>) };
        }
        // Prefer aiContent (simplified) if available, otherwise full screen
        const aiContent = (s as any).aiContent;
        return aiContent
          ? sanitizeStrategyScreenForRegenerationInput({
              id: (s as any).id,
              screenType: (s as any).screenType,
              content: aiContent,
            })
          : sanitizeStrategyScreenForRegenerationInput(s as any);
      }),
    };

    const payload: AgentUpdateBlockRequest = {
      projectId,
      strategyId,
      blockId,
      blockType: (block as any).blockType || 'unknown',
      prompt,
      sourceData: cleanSourceDataForAI((strategy as any).sourceData ?? {}) as any,
      userId,
      currentBlock,
      blockContract: (block as any).blockContract,
      strategyVariables: (strategy as any).variables,
    };

    return {
      __segmently_block_export__: true,
      version: '1.0.0',
      type: 'update-block',
      payload,
      metadata: {
        exportedAt: new Date().toISOString(),
        projectId,
        strategyId,
        blockId,
        blockName: (block as any).name,
        assetWorkspace,
      },
    };
  }

  /**
   * Block Examples Library capture: portable export + block-scoped variable
   * CLOSURE (AC-P1-003/004/005). `exportBlockPayload` DEFAULTS to the
   * regeneration workspace (simplified screens) and the WHOLE strategy
   * variable map — a library example must carry full StepNodes and only the
   * variables the block actually references. The pure core lives in
   * `blockLibraryCapture.ts` (shared with the system-tier fixture export
   * script); this method only loads the data via the client SDK.
   */
  async buildBlockLibraryCapture(
    projectId: string,
    strategyId: string,
    blockId: string,
  ): Promise<BlockLibraryCapture> {
    const strategyService = new StrategyService(projectId);
    const blockService = new StrategyBlockService(projectId, strategyId);
    const screenService = new StrategyStepNodeService(projectId, strategyId, blockId);

    const [strategy, block, screens] = await Promise.all([
      strategyService.get(strategyId),
      blockService.get(blockId),
      screenService.listScreens(),
    ]);

    if (!strategy) throw new Error(`Strategy ${strategyId} not found`);
    if (!block) throw new Error(`Block ${blockId} not found`);

    return buildBlockLibraryCaptureFromData({
      projectId,
      strategyId,
      blockId,
      strategy: strategy as unknown as Record<string, unknown>,
      block: block as unknown as Record<string, unknown>,
      screens: screens as unknown as Array<Record<string, unknown>>,
    });
  }

  /**
   * Download JSON data as a file.
   */
  static download(data: unknown, filename: string): void {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
